import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../design-system'
import { Brain, ArrowRight, Trophy, Users, Clock, Star, Award } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import AuthModal from '../components/AuthModal'

const HomePage = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isAuthenticated, isGuest } = useAuth();
  const navigate = useNavigate();

  const handleQuickTest = () => {
    navigate('/test?mode=quick&count=10');
  };

  const handleComprehensiveTest = () => {
    if (!isAuthenticated && !isGuest) {
      setShowAuthModal(true);
    } else {
      navigate('/test?mode=comprehensive&count=25');
    }
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    // After closing modal, if user chose an option (guest or auth), navigate to test
    setTimeout(() => {
      navigate('/test?mode=comprehensive&count=25');
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-8">
        <div className="text-center max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
              Test Your IQ
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-6">
              Discover your cognitive abilities with our scientifically validated IQ assessment
            </p>
          </div>

          {/* Test Options */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8 md:grid-rows-1">
            {/* Quick Test */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 flex flex-col">
              <div className="flex items-center justify-center mb-4 gap-3">
                <Clock className="w-8 h-8 text-blue-600 flex-shrink-0 relative -top-2" />
                <h3 className="text-2xl font-semibold text-gray-900">Quick Test</h3>
              </div>
              <div className="flex-1">
                <div className="text-3xl font-bold text-blue-600 mb-2">10 Questions</div>
                <p className="text-gray-600 mb-4">Perfect for a quick assessment. Get your IQ score in 5-10 minutes.</p>
                <ul className="text-sm text-gray-500 mb-6 space-y-1">
                  <li>• No registration required</li>
                  <li>• Basic IQ assessment</li>
                  <li>• Quick results</li>
                  <li className="invisible">• Placeholder</li>
                </ul>
              </div>
              <div>
                <button
                  onClick={handleQuickTest}
                  className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center"
                >
                  Quick Test
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
                <div className="h-6"></div>
              </div>
            </div>

            {/* Comprehensive Test */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 relative flex flex-col">
              <div className="absolute top-4 right-4">
                <Award className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex items-center justify-center mb-4 gap-3">
                <Star className="w-8 h-8 text-purple-600 flex-shrink-0 relative -top-2" />
                <h3 className="text-2xl font-semibold text-gray-900">Comprehensive Test</h3>
              </div>
              <div className="flex-1">
                <div className="text-3xl font-bold text-purple-600 mb-2">25 Questions</div>
                <p className="text-gray-600 mb-4">Complete IQ assessment with detailed analysis and leaderboard submission.</p>
                <ul className="text-sm text-gray-500 mb-6 space-y-1">
                  <li>• Detailed cognitive analysis</li>
                  <li>• Leaderboard eligibility (with login)</li>
                  <li>• Comprehensive report</li>
                  <li>• Guest option available</li>
                </ul>
              </div>
              <div>
                <button
                  onClick={handleComprehensiveTest}
                  className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium flex items-center justify-center shadow-sm"
                >
                  Comprehensive Test
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
                <div className="h-6">
                  {!isAuthenticated && !isGuest && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Login required for leaderboard submission
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Leaderboard Link */}
          <div className="mb-8">
            <Link to="/leaderboard">
              <button className="px-6 py-3 text-base font-medium bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center mx-auto opacity-90 hover:opacity-100">
                <Trophy className="mr-2 w-5 h-5" />
                View Leaderboard
              </button>
            </Link>
          </div>
        </div>
        
        {/* Features */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="text-center p-6 bg-white rounded-lg shadow-sm">
            <Clock className="w-12 h-12 mx-auto text-primary-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Quick & Accurate</h3>
            <p className="text-gray-600">
              Get your IQ score in just 10-25 minutes with scientifically validated questions
            </p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-lg shadow-sm">
            <Brain className="w-12 h-12 mx-auto text-secondary-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Comprehensive Analysis</h3>
            <p className="text-gray-600">
              Detailed breakdown of your cognitive abilities across multiple categories
            </p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-lg shadow-sm">
            <Users className="w-12 h-12 mx-auto text-success-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Compare & Compete</h3>
            <p className="text-gray-600">
              See how you rank against thousands of other test takers worldwide
            </p>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleAuthModalClose}
        title="Choose How to Take the Comprehensive Test"
        subtitle="Sign in to save your score to the leaderboard, or continue as a guest. Note: Only registered users can submit scores to the leaderboard."
      />
    </div>
  )
}

export default HomePage
